/**
 * The platform ↔ giving API contracts (Phase 5, docs/phase5-design.md §b/§d).
 *
 * These are the WIRE types: the platform developer implements against them,
 * and the giving system's routes parse with them. They live in `shared` so a
 * drift between the two sides is a type error at build time, not a 400 in
 * production. Money is integer cents (README rule 1); seats are counts; a
 * student's identity appears NOWHERE in these types — the giving system never
 * learns it.
 */
import { z } from 'zod';

/** The code states, in wire vocabulary. Same set as the DB column. */
export const REDEMPTION_STATE = z.enum([
  'ISSUED',
  'NOTICE',
  'CLAIMED',
  'QUEUED',
  'REDEEMED',
  'EXPIRED',
  'CANCELLED',
]);
export type RedemptionState = z.infer<typeof REDEMPTION_STATE>;

/** POST /api/platform/v1/codes/verify — request. */
export const CodeVerifyRequest = z.object({
  code: z.string().min(20).max(200),
});
export type CodeVerifyRequest = z.infer<typeof CodeVerifyRequest>;

/**
 * POST /api/platform/v1/codes/verify — response. Read-only: seats, track,
 * state, expiry. NO donor data exists to send.
 */
export const CodeVerifyResponse = z.object({
  seats: z.number().int().positive(),
  trackKey: z.string().nullable(),
  trackTitle: z.string().nullable(),
  expiresAt: z.string(),
  state: REDEMPTION_STATE,
});
export type CodeVerifyResponse = z.infer<typeof CodeVerifyResponse>;

/** POST /api/platform/v1/codes/claim — request. Idempotent on (code, ref). */
export const CodeClaimRequest = z.object({
  code: z.string().min(20).max(200),
  /** The platform's own idempotency reference — a UUID it mints per redemption. */
  platformRedemptionRef: z.string().uuid(),
  /** True when the platform has queued the credit for its own batch. */
  queued: z.boolean().optional(),
});
export type CodeClaimRequest = z.infer<typeof CodeClaimRequest>;

/** POST /api/platform/v1/codes/claim — response. */
export const CodeClaimResponse = z.object({
  seats: z.number().int().positive(),
  trackKey: z.string().nullable(),
  expiresAt: z.string(),
  claimExpiresAt: z.string(),
  state: REDEMPTION_STATE,
  replayed: z.boolean().optional(),
});
export type CodeClaimResponse = z.infer<typeof CodeClaimResponse>;

/** POST /api/platform/v1/codes/confirm — request. Idempotent on the ref. */
export const CodeConfirmRequest = z.object({
  platformRedemptionRef: z.string().uuid(),
});
export type CodeConfirmRequest = z.infer<typeof CodeConfirmRequest>;

/** POST /api/platform/v1/codes/confirm — response. */
export const CodeConfirmResponse = z.object({
  state: z.literal('REDEEMED'),
  redeemedAt: z.string(),
});
export type CodeConfirmResponse = z.infer<typeof CodeConfirmResponse>;

/** POST /api/platform/v1/codes/cancel — request. */
export const CodeCancelRequest = z.object({
  platformRedemptionRef: z.string().uuid(),
  reason: z.string().min(1).max(200),
});
export type CodeCancelRequest = z.infer<typeof CodeCancelRequest>;

/** POST /api/platform/v1/codes/cancel — response. */
export const CodeCancelResponse = z.object({
  state: REDEMPTION_STATE,
});
export type CodeCancelResponse = z.infer<typeof CodeCancelResponse>;

/**
 * The giving → platform aggregate push (one direction: the giving system
 * signs and sends; the platform stores or ignores). Idempotent on
 * (period, trackKey). Aggregates only — never a student, never a code.
 */
export const GivingReportPayload = z.object({
  period: z.string().regex(/^\d{4}-\d{2}$/),
  trackKey: z.string().nullable(),
  seatsFunded: z.number().int().nonnegative(),
  seatsRedeemed: z.number().int().nonnegative(),
  seatsExpired: z.number().int().nonnegative(),
});
export type GivingReportPayload = z.infer<typeof GivingReportPayload>;

/**
 * The signature headers every platform-API request carries. The signature is
 * HMAC-SHA256 over `METHOD\nPATH\nTIMESTAMP\nNONCE\nsha256hex(BODY)`.
 */
export const SIGNATURE_HEADERS = {
  key: 'x-qalam-key',
  timestamp: 'x-qalam-timestamp',
  nonce: 'x-qalam-nonce',
  signature: 'x-qalam-signature',
} as const;
